import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	Snackbar,
	Stack,
	Switch,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { Redirect } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncIcon from '@mui/icons-material/Sync';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Layout } from 'features/common/components/Layout';
import { isAdminOrSuperAdmin } from 'features/auth/helpers';
import routes from 'constants/routes';
import { useAdminStepClassification } from 'features/salesDashboard/hooks/useAdminStepClassification';
import { useWorkflowExclusions } from 'features/salesDashboard/hooks/useWorkflowExclusions';
import { useSyncStatus } from 'features/salesDashboard/hooks/useSyncStatus';
import { getDashboardWorkflows, putDashboardWorkflows } from 'features/salesDashboard/services';
import { IDashboardWorkflowItem, StepClassification, StepClassificationWritable } from 'features/salesDashboard/types';
import { resolveErrorMessage } from 'features/salesDashboard/hooks/hookUtils';

// ─── Constante: agencia operativa (Service Center) ───────────────────────────

const OPERATIVE_AGENCY_ID = 1;

// ─── Subcomponente: ID de workflow copiable ───────────────────────────────────

function CopyableWorkflowId({ workflowId }: { workflowId: string }): JSX.Element {
	const [copied, setCopied] = useState(false);
	const handleCopy = (e: React.MouseEvent) => {
		e.stopPropagation();
		navigator.clipboard.writeText(workflowId);
		setCopied(true);
		setTimeout(() => setCopied(false), 1500);
	};
	return (
		<Box display="flex" alignItems="center" gap={0.25}>
			<Typography
				variant="caption"
				color="text.disabled"
				sx={{ fontFamily: 'monospace', fontSize: '0.65rem', wordBreak: 'break-all' }}
			>
				{workflowId}
			</Typography>
			<Tooltip title={copied ? 'Copiado' : 'Copiar workflow ID'}>
				<IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
					{copied ? (
						<CheckIcon sx={{ fontSize: 13, color: 'success.main' }} />
					) : (
						<ContentCopyIcon sx={{ fontSize: 13 }} />
					)}
				</IconButton>
			</Tooltip>
		</Box>
	);
}

// ─── Subcomponente: Clasificación de steps ───────────────────────────────────
// Solo para agencias de VENTAS (agencyId ≠ OPERATIVE_AGENCY_ID)

function StepClassificationSection(): JSX.Element {
	const [filterStatus, setFilterStatus] = useState<StepClassification | 'ALL'>('PENDING_REVIEW');
	const [filterAgencyId, setFilterAgencyId] = useState<number | undefined>(undefined);
	const [filterWorkflowId, setFilterWorkflowId] = useState<string | undefined>(undefined);
	const [includeExcluded, setIncludeExcluded] = useState(false);

	const { data, loading, error, patching, classify } = useAdminStepClassification(
		filterStatus === 'ALL' ? undefined : filterStatus,
		filterAgencyId,
		includeExcluded,
	);

	// Agencias de venta (excluye agencia operativa)
	const agencies = useMemo(() => {
		if (!data) return [];
		const seen = new Map<number, string>();
		data.forEach((item) => {
			if (item.agencyId == null || item.agencyId === OPERATIVE_AGENCY_ID) return;
			if (!seen.has(item.agencyId)) seen.set(item.agencyId, item.agencyName ?? `Agencia ${item.agencyId}`);
		});
		return Array.from(seen.entries())
			.map(([id, name]) => ({ id, name }))
			.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
	}, [data]);

	// Workflows (excluye los de agencia operativa)
	const workflows = useMemo(() => {
		if (!data) return [];
		const seen = new Map<string, string>();
		data.forEach((item) => {
			if (!item.workflowId || item.agencyId === OPERATIVE_AGENCY_ID) return;
			if (!seen.has(item.workflowId)) seen.set(item.workflowId, item.workflowName ?? item.workflowId);
		});
		return Array.from(seen.entries())
			.map(([id, name]) => ({ id, name }))
			.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
	}, [data]);

	// Filtro de workflow + excluir agencia operativa de la tabla
	const displayData = useMemo(() => {
		if (!data) return data;
		return data.filter(
			(item) => item.agencyId !== OPERATIVE_AGENCY_ID && (!filterWorkflowId || item.workflowId === filterWorkflowId),
		);
	}, [data, filterWorkflowId]);

	const FILTERS: Array<{ value: StepClassification | 'ALL'; label: string }> = [
		{ value: 'PENDING_REVIEW', label: 'Pendientes' },
		{ value: 'OPEN', label: 'Abiertos' },
		{ value: 'WON', label: 'Ganados' },
		{ value: 'LOST', label: 'Perdidos' },
		{ value: 'ALL', label: 'Todos' },
	];

	return (
		<Box>
			<Typography variant="subtitle1" fontWeight={600} mb={0.5}>
				Clasificación de steps del CRM
			</Typography>
			<Typography variant="caption" color="text.secondary" display="block" mb={2}>
				Solo agencias de venta (el Service Center se gestiona desde los dashboards). Marcá cada step como OPEN, WON o
				LOST. El campo &quot;Sugerencia&quot; es automático y <strong>puede estar equivocado</strong> — tomalo solo como
				referencia.
			</Typography>

			<Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={1}>
				<Tabs
					value={filterStatus}
					onChange={(_, val) => setFilterStatus(val)}
					variant="scrollable"
					scrollButtons="auto"
					sx={{ flex: 1, minWidth: 0 }}
				>
					{FILTERS.map((f) => (
						<Tab key={f.value} value={f.value} label={f.label} />
					))}
				</Tabs>
				<TextField
					select
					label="Agencia"
					size="small"
					value={filterAgencyId ?? ''}
					onChange={(e) => {
						setFilterAgencyId(e.target.value !== '' ? Number(e.target.value) : undefined);
						setFilterWorkflowId(undefined);
					}}
					sx={{ minWidth: 160 }}
				>
					<MenuItem value="">Todas las agencias</MenuItem>
					{agencies.map((a) => (
						<MenuItem key={a.id} value={a.id}>
							{a.name}
						</MenuItem>
					))}
				</TextField>
				<TextField
					select
					label="Workflow"
					size="small"
					value={filterWorkflowId ?? ''}
					onChange={(e) => setFilterWorkflowId(e.target.value !== '' ? String(e.target.value) : undefined)}
					sx={{ minWidth: 200 }}
				>
					<MenuItem value="">Todos los workflows</MenuItem>
					{workflows.map((w) => (
						<MenuItem key={w.id} value={w.id}>
							{w.name}
						</MenuItem>
					))}
				</TextField>
				<FormControlLabel
					control={
						<Switch size="small" checked={includeExcluded} onChange={(e) => setIncludeExcluded(e.target.checked)} />
					}
					label={<Typography variant="caption">Ver excluidos</Typography>}
					sx={{ mr: 0 }}
				/>
			</Box>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{loading ? (
				<Skeleton variant="rectangular" height={200} />
			) : (
				<TableContainer component={Paper} variant="outlined">
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Agencia / Workflow</TableCell>
								<TableCell>Step</TableCell>
								<TableCell>Sugerencia automática</TableCell>
								<TableCell>Clasificación actual</TableCell>
								<TableCell>Revisado por</TableCell>
								<TableCell>Revisado el</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{displayData && displayData.length > 0 ? (
								displayData.map((item) => (
									<TableRow key={item.stepId} hover>
										<TableCell>
											<Typography variant="body2" fontWeight={500}>
												{item.agencyName}
											</Typography>
											<Typography variant="caption" color="text.secondary" display="block">
												{item.workflowName}
											</Typography>
											{item.workflowId && <CopyableWorkflowId workflowId={item.workflowId} />}
										</TableCell>
										<TableCell>
											<Typography variant="body2">{item.stepName}</Typography>
										</TableCell>
										<TableCell>
											{item.suggested ? (
												<Tooltip title="Esta sugerencia es automática y puede estar equivocada. No se aplica sola.">
													<Chip
														label={`Sugerencia: ${item.suggested}`}
														size="small"
														sx={{ bgcolor: 'grey.200', color: 'text.secondary', cursor: 'help' }}
													/>
												</Tooltip>
											) : (
												<Typography variant="caption" color="text.disabled">
													—
												</Typography>
											)}
										</TableCell>
										<TableCell>
											{patching === item.stepId ? (
												<CircularProgress size={20} />
											) : (
												<FormControl size="small" sx={{ minWidth: 110 }}>
													<Select
														value={item.classification === 'PENDING_REVIEW' ? '' : item.classification}
														displayEmpty
														onChange={(e) => {
															if (e.target.value) classify(item.stepId, e.target.value as StepClassificationWritable);
														}}
													>
														<MenuItem value="" disabled>
															<em>Seleccionar…</em>
														</MenuItem>
														<MenuItem value="OPEN">OPEN</MenuItem>
														<MenuItem value="WON">WON</MenuItem>
														<MenuItem value="LOST">LOST</MenuItem>
													</Select>
												</FormControl>
											)}
										</TableCell>
										<TableCell>
											<Typography variant="caption">{item.reviewedBy ?? '—'}</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="caption">
												{item.reviewedAt ? new Date(item.reviewedAt).toLocaleDateString('es') : '—'}
											</Typography>
										</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>
										No hay steps en este estado
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
	);
}

// ─── Subcomponente: Workflows del dashboard operativo ─────────────────────────

function DashboardWorkflowsSection(): JSX.Element {
	const [items, setItems] = useState<IDashboardWorkflowItem[] | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState(false);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [fetchCount, setFetchCount] = useState(0);

	const refetch = useCallback(() => setFetchCount((n) => n + 1), []);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);

		getDashboardWorkflows()
			.then((data) => {
				if (cancelled) return;
				setItems(data);
				setSelected(new Set(data.filter((w) => w.selected).map((w) => w.workflowId)));
				setLoading(false);
			})
			.catch((err) => {
				if (cancelled) return;
				setError(resolveErrorMessage(err));
				setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [fetchCount]);

	const handleToggle = (workflowId: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(workflowId)) next.delete(workflowId);
			else next.add(workflowId);
			return next;
		});
	};

	const handleSave = async () => {
		setSaving(true);
		setError(null);
		try {
			await putDashboardWorkflows(Array.from(selected));
			setSaved(true);
			setTimeout(() => setSaved(false), 3000);
		} catch (err) {
			setError(resolveErrorMessage(err));
		} finally {
			setSaving(false);
		}
	};

	// Agrupa por área para facilitar la lectura
	const byArea = useMemo(() => {
		if (!items) return [];
		const map = new Map<string, IDashboardWorkflowItem[]>();
		items.forEach((item) => {
			const area = item.area || '—';
			if (!map.has(area)) map.set(area, []);
			const bucket = map.get(area);
			if (bucket) bucket.push(item);
		});
		return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
	}, [items]);

	const selectedCount = selected.size;
	const isDirty = useMemo(() => {
		if (!items) return false;
		const original = new Set(items.filter((w) => w.selected).map((w) => w.workflowId));
		if (original.size !== selected.size) return true;
		for (const id of selected) {
			if (!original.has(id)) return true;
		}
		return false;
	}, [items, selected]);

	return (
		<Box>
			<Typography variant="subtitle1" fontWeight={600} mb={0.5}>
				Workflows del dashboard operativo
			</Typography>
			<Typography variant="caption" color="text.secondary" display="block" mb={2}>
				Elegí qué workflows de la agencia operativa aparecen en el widget &quot;Necesidades abiertas&quot; de los
				dashboards de vendedor y equipo. El número entre paréntesis son los casos abiertos hoy.
			</Typography>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{loading ? (
				<Skeleton variant="rectangular" height={300} />
			) : items && items.length > 0 ? (
				<>
					<Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
						<Typography variant="body2" color="text.secondary">
							{selectedCount} de {items.length} workflows seleccionados
						</Typography>
						<Stack direction="row" spacing={1}>
							<Button size="small" variant="outlined" onClick={refetch} disabled={loading || saving}>
								Refrescar
							</Button>
							<Button
								size="small"
								variant="contained"
								startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
								onClick={handleSave}
								disabled={saving || !isDirty}
								color={saved ? 'success' : 'primary'}
							>
								{saved ? 'Guardado' : 'Guardar selección'}
							</Button>
						</Stack>
					</Box>

					{byArea.map(([area, workflows]) => (
						<Box key={area} mb={3}>
							<Typography variant="overline" color="text.secondary" display="block" mb={0.5}>
								Área: {area}
							</Typography>
							<TableContainer component={Paper} variant="outlined">
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell padding="checkbox" />
											<TableCell>Workflow</TableCell>
											<TableCell align="right">Total casos</TableCell>
											<TableCell align="right">Abiertos hoy</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{workflows.map((w) => (
											<TableRow
												key={w.workflowId}
												hover
												onClick={() => handleToggle(w.workflowId)}
												sx={{ cursor: 'pointer' }}
											>
												<TableCell padding="checkbox">
													<Switch
														size="small"
														checked={selected.has(w.workflowId)}
														onChange={() => handleToggle(w.workflowId)}
														onClick={(e) => e.stopPropagation()}
													/>
												</TableCell>
												<TableCell>
													<Typography variant="body2" fontWeight={selected.has(w.workflowId) ? 600 : 400}>
														{w.workflowName}
													</Typography>
												</TableCell>
												<TableCell align="right">
													<Typography variant="body2" color="text.secondary">
														{w.caseCount}
													</Typography>
												</TableCell>
												<TableCell align="right">
													{w.openCaseCount > 0 ? (
														<Chip label={w.openCaseCount} size="small" color="warning" />
													) : (
														<Typography variant="body2" color="text.disabled">
															0
														</Typography>
													)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					))}
				</>
			) : (
				<Alert severity="info">
					No hay workflows operativos disponibles. Verificá que la sincronización esté al día.
				</Alert>
			)}
		</Box>
	);
}

// ─── Subcomponente: Exclusión de workflows ───────────────────────────────────

function WorkflowExclusionsSection(): JSX.Element {
	const { data, loading, error, submitting, addExclusion, removeExclusion } = useWorkflowExclusions();
	const [newWorkflowId, setNewWorkflowId] = useState('');
	const [newReason, setNewReason] = useState('');

	const handleAdd = async () => {
		if (!newWorkflowId.trim() || !newReason.trim()) return;
		await addExclusion(newWorkflowId.trim(), newReason.trim());
		setNewWorkflowId('');
		setNewReason('');
	};

	return (
		<Box>
			<Typography variant="subtitle1" fontWeight={600} mb={1}>
				Workflows excluidos de las métricas
			</Typography>
			<Typography variant="caption" color="text.secondary" display="block" mb={2}>
				Los casos de workflows excluidos no cuentan en conversión ni pipeline. Una vez excluido, sus steps desaparecen
				de la vista de clasificación (usá el toggle &quot;Ver excluidos&quot; allá para seguir viéndolos).
			</Typography>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{loading ? (
				<Skeleton variant="rectangular" height={120} sx={{ mb: 2 }} />
			) : (
				<TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Workflow ID</TableCell>
								<TableCell>Motivo</TableCell>
								<TableCell>Excluido el</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{data && data.length > 0 ? (
								data.map((item) => (
									<TableRow key={item.workflowId} hover>
										<TableCell>
											<Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
												{item.workflowId}
											</Typography>
										</TableCell>
										<TableCell>{item.reason}</TableCell>
										<TableCell>{new Date(item.excludedAt).toLocaleDateString('es')}</TableCell>
										<TableCell>
											<IconButton
												size="small"
												color="error"
												onClick={() => removeExclusion(item.workflowId)}
												title="Quitar exclusión"
											>
												<DeleteIcon fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>
										No hay workflows excluidos
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<Paper variant="outlined" sx={{ p: 2 }}>
				<Typography variant="subtitle2" fontWeight={600} mb={1.5}>
					Agregar exclusión
				</Typography>
				<Grid container spacing={2} alignItems="flex-end">
					<Grid item xs={12} sm={5}>
						<TextField
							label="Workflow ID"
							value={newWorkflowId}
							onChange={(e) => setNewWorkflowId(e.target.value)}
							size="small"
							fullWidth
							placeholder="uuid del workflow"
						/>
					</Grid>
					<Grid item xs={12} sm={5}>
						<TextField
							label="Motivo"
							value={newReason}
							onChange={(e) => setNewReason(e.target.value)}
							size="small"
							fullWidth
							placeholder="ej: workflow de prueba"
						/>
					</Grid>
					<Grid item xs={12} sm={2}>
						<Button
							variant="contained"
							onClick={handleAdd}
							disabled={submitting || !newWorkflowId.trim() || !newReason.trim()}
							fullWidth
						>
							{submitting ? <CircularProgress size={20} /> : 'Agregar'}
						</Button>
					</Grid>
				</Grid>
			</Paper>
		</Box>
	);
}

// ─── Subcomponente: Estado de sync ────────────────────────────────────────────

function SyncStatusSection(): JSX.Element {
	const { data, loading, error, firing, refetch, syncMetadata, syncTrigger, syncBackfill } = useSyncStatus();

	const [backfillDialogOpen, setBackfillDialogOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

	const handleSyncMetadata = async () => {
		try {
			const result = await syncMetadata();
			setSnackbarMessage(result.message ?? 'Sincronización de metadata iniciada');
		} catch {
			// el error ya lo maneja el hook
		}
	};

	const handleSyncTrigger = async () => {
		try {
			const result = await syncTrigger();
			setSnackbarMessage(result.message ?? 'Sincronización de casos iniciada');
		} catch {
			// el error ya lo maneja el hook
		}
	};

	const handleBackfillConfirm = async () => {
		setBackfillDialogOpen(false);
		try {
			const result = await syncBackfill();
			setSnackbarMessage(result.message ?? 'Backfill iniciado — puede tardar varios minutos');
		} catch {
			// el error ya lo maneja el hook
		}
	};

	function formatRelative(iso: string): string {
		const diff = Date.now() - new Date(iso).getTime();
		const mins = Math.floor(diff / 60000);
		if (mins < 1) return 'hace menos de 1 min';
		if (mins < 60) return `hace ${mins} min`;
		const h = Math.floor(mins / 60);
		if (h < 24) return `hace ${h}h`;
		return `hace ${Math.floor(h / 24)}d`;
	}

	return (
		<Box>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
				<Typography variant="subtitle1" fontWeight={600}>
					Estado de sincronización con el CRM
				</Typography>
				<Button size="small" startIcon={<SyncIcon />} onClick={refetch} disabled={loading}>
					Refrescar
				</Button>
			</Box>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{loading ? (
				<Skeleton variant="rectangular" height={100} sx={{ mb: 2 }} />
			) : (
				<TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Job</TableCell>
								<TableCell>Última sincronización</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{data && data.length > 0 ? (
								data.map((item) => (
									<TableRow key={item.key}>
										<TableCell>
											<Typography variant="body2" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
												{item.key}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2">{formatRelative(item.lastSyncedAt)}</Typography>
											<Typography variant="caption" color="text.secondary">
												{new Date(item.lastSyncedAt).toLocaleString('es')}
											</Typography>
										</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={2} align="center" sx={{ color: 'text.secondary' }}>
										Sin datos de estado
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<Box display="flex" gap={2} flexWrap="wrap">
				<Tooltip title="Sincroniza workflows y agentes (rápido, sin casos)">
					<span>
						<Button
							variant="outlined"
							startIcon={firing === 'metadata' ? <CircularProgress size={16} /> : <SyncIcon />}
							onClick={handleSyncMetadata}
							disabled={firing !== null}
						>
							Sincronizar workflows y agentes
						</Button>
					</span>
				</Tooltip>

				<Tooltip title="Sincroniza casos incrementalmente (actualiza lo modificado recientemente)">
					<span>
						<Button
							variant="outlined"
							startIcon={firing === 'trigger' ? <CircularProgress size={16} /> : <SyncIcon />}
							onClick={handleSyncTrigger}
							disabled={firing !== null}
						>
							Sincronizar casos
						</Button>
					</span>
				</Tooltip>

				<Tooltip title="Re-sincroniza TODO el historial. Puede tardar varios minutos.">
					<span>
						<Button
							variant="outlined"
							color="warning"
							startIcon={firing === 'backfill' ? <CircularProgress size={16} /> : <SyncIcon />}
							onClick={() => setBackfillDialogOpen(true)}
							disabled={firing !== null}
						>
							Backfill completo
						</Button>
					</span>
				</Tooltip>
			</Box>

			<Dialog open={backfillDialogOpen} onClose={() => setBackfillDialogOpen(false)}>
				<DialogTitle>¿Iniciar backfill completo?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Esta operación re-sincroniza todo el historial de casos desde el CRM. Puede tardar
						<strong> varios minutos</strong> y genera carga en el backend. El proceso corre en segundo plano; no
						necesitás esperar acá.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setBackfillDialogOpen(false)}>Cancelar</Button>
					<Button onClick={handleBackfillConfirm} color="warning" variant="contained">
						Iniciar backfill
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbarMessage !== null}
				autoHideDuration={5000}
				onClose={() => setSnackbarMessage(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				message={snackbarMessage}
			/>
		</Box>
	);
}

// ─── Pantalla principal ───────────────────────────────────────────────────────

export function CrmWorkflowsAdmin(): JSX.Element {
	const user = useSelector((state: RootState) => state.auth.user, shallowEqual);
	const [activeSection, setActiveSection] = useState(0);

	if (!isAdminOrSuperAdmin(user)) {
		return <Redirect to={user?.name ? routes.salesDashboard.seller(user.name) : routes.home} />;
	}

	return (
		<Layout>
			<Container maxWidth="xl" sx={{ py: 3 }}>
				<Typography variant="h5" fontWeight={700} color="primary" mb={1}>
					Administración de Workflows CRM
				</Typography>
				<Typography variant="body2" color="text.secondary" mb={3}>
					Configuración de clasificación, exclusiones, workflows del dashboard y sincronización.
				</Typography>

				<Tabs
					value={activeSection}
					onChange={(_, val) => setActiveSection(val)}
					sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
				>
					<Tab label="Clasificación de steps" />
					<Tab label="Workflows del dashboard" />
					<Tab label="Exclusión de workflows" />
					<Tab label="Estado de sincronización" />
				</Tabs>

				{activeSection === 0 && <StepClassificationSection />}
				{activeSection === 1 && <DashboardWorkflowsSection />}
				{activeSection === 2 && <WorkflowExclusionsSection />}
				{activeSection === 3 && <SyncStatusSection />}
			</Container>
		</Layout>
	);
}
